import React from 'react';

import { useDispatch } from 'react-redux';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';

import MultiSelectMenu, { EmployeeOption } from '../MultiStepMenu';
import {
  setModifiedStakeholdersRows,
  setModifiedStakeholdersTreeRows,
  useCompensationDrawersState,
} from '../slice/compensationDrawersSlice';
import {
  STAKEHOLDER_OPTIONS_PROPOSER,
  STAKEHOLDER_OPTIONS_APPROVER,
  STAKEHOLDER_OPTIONS_PUBLISHER,
} from './stakeholderConstants';

interface SelectedEmployee {
  id: number;
  value: string;
  label: string;
  profilePhoto?: string;
}

interface StakeholderRow {
  id: string;
  HIERARCHY: string[];
  [key: string]: unknown;
}

interface StakeholderRows extends Array<StakeholderRow> {}

interface OptionConfig {
  value: string;
  label: string;
  isSubMenu?: boolean;
}

/**
 * Get all descendant nodes (children) of a given node in the hierarchy
 */
const getDescendants = (nodeId: string, allRows: StakeholderRows): StakeholderRows => {
  const targetNode = allRows.find((row) => row.id === nodeId);
  if (!targetNode) return [];

  const descendants: StakeholderRows = [];
  const targetHierarchy = targetNode.HIERARCHY;

  allRows.forEach((row) => {
    // Check if this row is a descendant
    if (
      row.HIERARCHY.length > targetHierarchy.length &&
      row.HIERARCHY.slice(0, targetHierarchy.length).join(',') === targetHierarchy.join(',')
    ) {
      descendants.push(row);
    }
  });

  return descendants;
};

/**
 * Check if a value is unchanged by comparing modified row with original row
 */
const isValueUnchanged = (modifiedRow: StakeholderRow, originalRow: StakeholderRow, columnId: string): boolean => {
  // Check if the main column value is unchanged
  const isMainValueUnchanged = modifiedRow[columnId] === originalRow[columnId];

  // Check if the selected employees are unchanged
  const modifiedEmployees = modifiedRow[`${columnId.toLowerCase()}SelectedEmployees`] as EmployeeOption[] | null;
  const originalEmployees = originalRow[`${columnId.toLowerCase()}SelectedEmployees`] as EmployeeOption[] | null;

  const isEmployeesUnchanged =
    modifiedEmployees === originalEmployees ||
    (modifiedEmployees &&
      originalEmployees &&
      modifiedEmployees.length === originalEmployees.length &&
      modifiedEmployees.every((modifiedEmp, index) => {
        const originalEmp = originalEmployees[index];
        return (
          modifiedEmp.id === originalEmp.id &&
          modifiedEmp.label === originalEmp.label &&
          modifiedEmp.subTitle === originalEmp.subTitle
        );
      }));

  return isMainValueUnchanged && isEmployeesUnchanged;
};

/**
 * Apply cascading updates to stakeholder rows
 */
const applyCascadingStakeholderUpdates = (
  updatedNodeId: string,
  columnId: string,
  newValue: string,
  newEmployees: EmployeeOption[],
  modifiedRows: StakeholderRows,
  originalRows: StakeholderRows,
  applyToAllLevels: boolean
): StakeholderRows => {
  let updatedRows = [...modifiedRows];

  // Update the current node
  updatedRows = updatedRows.map((row) => {
    if (row.id === updatedNodeId) {
      const updatedRow = {
        ...row,
        [columnId]: newValue,
      };

      // Update selected employees if applicable
      if (newEmployees && newEmployees.length > 0) {
        updatedRow[`${columnId.toLowerCase()}SelectedEmployees`] = newEmployees.map((employee) => ({
          label: employee.label,
          value: employee.subTitle,
          profilePhoto: employee.profilePhoto,
          id: employee.id,
        }));
      } else {
        updatedRow[`${columnId.toLowerCase()}SelectedEmployees`] = [];
      }

      return updatedRow;
    }
    return row;
  });

  // Get descendants
  const descendants = getDescendants(updatedNodeId, updatedRows);

  // Apply changes to descendants based on the applyToAllLevels flag
  updatedRows = updatedRows.map((row) => {
    if (descendants.find((desc) => desc.id === row.id)) {
      let shouldUpdate = false;

      if (applyToAllLevels) {
        // Apply to all descendants
        shouldUpdate = true;
      } else {
        // Apply only to unchanged values
        const originalRow = originalRows.find((orig) => orig.id === row.id);
        if (originalRow) {
          shouldUpdate = isValueUnchanged(row, originalRow, columnId);
        }
      }

      if (shouldUpdate) {
        const updatedRow = {
          ...row,
          [columnId]: newValue,
        };

        // Update selected employees if applicable
        if (newEmployees && newEmployees.length > 0) {
          updatedRow[`${columnId.toLowerCase()}SelectedEmployees`] = newEmployees.map((employee) => ({
            label: employee.label,
            value: employee.subTitle,
            profilePhoto: employee.profilePhoto,
            id: employee.id,
          }));
        } else {
          updatedRow[`${columnId.toLowerCase()}SelectedEmployees`] = [];
        }

        return updatedRow;
      }
    }
    return row;
  });

  return updatedRows;
};

export const createMultiSelectMenuRendererTreeTable =
  () => (props: { data: StakeholderRow; colDef: { columnId: string }; options: Record<string, OptionConfig> }) => {
    const {
      data,
      colDef: { columnId },
      options,
    } = props ?? {};

    const { originalStakeholdersTreeRows: originalRows, modifiedStakeholdersTreeRows: modifiedRows } =
      useCompensationDrawersState();

    const dispatch = useDispatch();
    const rowId = data?.id as string;

    const onChange = ({
      value,
      employees = [],
      applyToAllLevels = false,
    }: {
      value: string;
      employees?: EmployeeOption[];
      applyToAllLevels?: boolean;
    }) => {
      const updatedRows = applyCascadingStakeholderUpdates(
        rowId,
        columnId,
        value,
        employees,
        modifiedRows,
        originalRows,
        applyToAllLevels
      );

      dispatch(setModifiedStakeholdersTreeRows(updatedRows));
    };

    return (
      <MultiSelectMenu data={data} colDef={{ columnId }} options={options} onChange={onChange} isCascadingAllowed />
    );
  };

export const createMultiSelectMenuRendererFlatList = () => (props) => {
  const {
    data,
    colDef: { columnId },
    options,
  } = props ?? {};

  const dispatch = useDispatch();
  const { modifiedStakeholdersRows } = useCompensationDrawersState();

  const rowId = data?.id;

  const onChange = ({ value, employees = [] }: { value: string; employees?: EmployeeOption[] }) => {
    const newModifiedStakeholdersRows = modifiedStakeholdersRows.map((row) => {
      if (row.id === rowId) {
        if (options.ANY_EMPLOYEE && value === options.ANY_EMPLOYEE.value) {
          return {
            ...row,
            [columnId]: value,
            [`${columnId.toLowerCase()}SelectedEmployees`]: employees.map((employee) => ({
              label: employee.label,
              value: employee.subTitle,
              profilePhoto: employee.profilePhoto,
              id: employee.id,
            })),
          };
        }
        return { ...row, [columnId]: value, [`${columnId.toLowerCase()}SelectedEmployees`]: [] };
      }
      return row;
    });
    dispatch(setModifiedStakeholdersRows(newModifiedStakeholdersRows));
  };

  return <MultiSelectMenu data={data} colDef={{ columnId }} options={options} onChange={onChange} />;
};

export const createAdditionalColumns = (MultiSelectMenuRenderer: React.ComponentType<unknown>) => [
  {
    columnId: 'PROPOSER',
    headerName: 'Proposer',
    cellRenderer: MultiSelectMenuRenderer,
    headerComponent: CustomHeaderCell,
    cellRendererParams: {
      options: { ANY_EMPLOYEE: STAKEHOLDER_OPTIONS_PROPOSER.ANY_EMPLOYEE },
    },
    minWidth: 240,
  },
  {
    columnId: 'APPROVER',
    headerName: 'Approver',
    cellRenderer: MultiSelectMenuRenderer,
    headerComponent: CustomHeaderCell,
    cellRendererParams: {
      options: { ANY_EMPLOYEE: STAKEHOLDER_OPTIONS_APPROVER.ANY_EMPLOYEE },
    },
    minWidth: 240,
  },
  {
    columnId: 'PUBLISHER',
    headerName: 'Publisher',
    cellRenderer: MultiSelectMenuRenderer,
    headerComponent: CustomHeaderCell,
    cellRendererParams: {
      options: STAKEHOLDER_OPTIONS_PUBLISHER,
    },
    minWidth: 240,
  },
];

export const getDefaultStakeholderValues = (rows: StakeholderRows): StakeholderRows =>
  rows.map((row) => ({
    ...row,
    PROPOSER: STAKEHOLDER_OPTIONS_PROPOSER.ANY_EMPLOYEE.value,
    APPROVER: STAKEHOLDER_OPTIONS_APPROVER.ANY_EMPLOYEE.value,
    PUBLISHER: STAKEHOLDER_OPTIONS_PUBLISHER.ANY_EMPLOYEE.value,
  }));

export const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
